"use client"

import React, { useState, useEffect, useCallback } from "react"
import { motion } from "framer-motion"
import { X, ImagePlus, Trash2, ImageOff, Link2, Building2 } from "lucide-react"
import {
  getFotosBySucursal,
  createFoto,
  deleteFoto,
} from "../services/sucursalFotografiaService"
import "./FotosSucursalModal.css"

/* Tarjeta de una foto con manejo de error de carga */
const FotoItem = ({ foto, onDelete, deleting }) => {
  const [err, setErr] = useState(false)
  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      className="fs-item"
    >
      <div className="fs-img-wrap">
        {err ? (
          <div className="fs-img-error"><ImageOff size={26} /><span>No se pudo cargar</span></div>
        ) : (
          <img src={foto.fotografia} alt="Foto de sucursal" onError={() => setErr(true)} loading="lazy" />
        )}
        <button className="fs-del" title="Eliminar foto" onClick={() => onDelete(foto)} disabled={deleting}>
          <Trash2 size={15} />
        </button>
      </div>
      <a className="fs-url" href={foto.fotografia} target="_blank" rel="noreferrer" title={foto.fotografia}>
        <Link2 size={12} /> {foto.fotografia}
      </a>
    </motion.div>
  )
}

const FotosSucursalModal = ({ sucursal, onClose }) => {
  const [fotos, setFotos] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [nuevaUrl, setNuevaUrl] = useState("")
  const [previewOk, setPreviewOk] = useState(true)
  const [saving, setSaving] = useState(false)
  const [deletingId, setDeletingId] = useState(null)

  const cargar = useCallback(async () => {
    try {
      setLoading(true)
      setError("")
      const data = await getFotosBySucursal(sucursal.id)
      setFotos(Array.isArray(data) ? data : [])
    } catch (e) {
      setError(e.message || "Error al cargar las fotos.")
    } finally {
      setLoading(false)
    }
  }, [sucursal.id])

  useEffect(() => { cargar() }, [cargar])

  useEffect(() => { setPreviewOk(true) }, [nuevaUrl])

  const handleAgregar = async (e) => {
    e.preventDefault()
    const url = nuevaUrl.trim()
    if (!url) return
    try {
      setSaving(true)
      setError("")
      const creada = await createFoto(sucursal.id, url)
      setFotos((prev) => [...prev, creada])
      setNuevaUrl("")
    } catch (err) {
      setError(err.message || "No se pudo agregar la foto.")
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (foto) => {
    if (!window.confirm("¿Eliminar esta foto de la sucursal?")) return
    try {
      setDeletingId(foto.id)
      await deleteFoto(foto.id)
      setFotos((prev) => prev.filter((f) => f.id !== foto.id))
    } catch (err) {
      setError(err.message || "No se pudo eliminar la foto.")
    } finally {
      setDeletingId(null)
    }
  }

  const urlValida = /^https?:\/\/.+/i.test(nuevaUrl.trim())

  return (
    <div className="fs-overlay" onClick={onClose}>
      <motion.div
        className="fs-modal"
        onClick={(e) => e.stopPropagation()}
        initial={{ opacity: 0, y: 24, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 24, scale: 0.97 }}
        transition={{ type: "spring", stiffness: 300, damping: 26 }}
      >
        <div className="fs-header">
          <h3><Building2 size={20} /> Fotos · {sucursal.nombre}</h3>
          <button className="fs-icon-btn" onClick={onClose} aria-label="Cerrar"><X size={20} /></button>
        </div>

        {/* Agregar nueva foto */}
        <form className="fs-add" onSubmit={handleAgregar}>
          <div className="fs-add-row">
            <div className="fs-input-wrap">
              <Link2 size={16} />
              <input
                value={nuevaUrl}
                onChange={(e) => setNuevaUrl(e.target.value)}
                placeholder="Pega la URL de la imagen (https://…)"
              />
            </div>
            <button type="submit" className="fs-btn-primary" disabled={!urlValida || saving}>
              <ImagePlus size={17} /> {saving ? "Agregando…" : "Agregar"}
            </button>
          </div>

          {/* Previsualización en vivo de la URL escrita */}
          {nuevaUrl.trim() && (
            <div className="fs-preview">
              <span className="fs-preview-label">Vista previa:</span>
              {urlValida && previewOk ? (
                <img src={nuevaUrl.trim()} alt="Vista previa" onError={() => setPreviewOk(false)} />
              ) : (
                <div className="fs-preview-error"><ImageOff size={18} /> {urlValida ? "No carga la imagen" : "URL no válida"}</div>
              )}
            </div>
          )}
        </form>

        {error && <div className="fs-error">{error}</div>}

        {/* Galería */}
        <div className="fs-body">
          {loading ? (
            <div className="fs-loading"><div className="fs-spinner" /> Cargando fotos…</div>
          ) : fotos.length === 0 ? (
            <div className="fs-empty"><ImageOff size={40} /><p>Esta sucursal aún no tiene fotos.</p></div>
          ) : (
            <div className="fs-grid">
              {fotos.map((foto) => (
                <FotoItem key={foto.id} foto={foto} onDelete={handleDelete} deleting={deletingId === foto.id} />
              ))}
            </div>
          )}
        </div>

        <div className="fs-footer">
          <span>{fotos.length} foto(s)</span>
          <button className="fs-btn-ghost" onClick={onClose}>Cerrar</button>
        </div>
      </motion.div>
    </div>
  )
}

export default FotosSucursalModal
